import React from 'react';
import {
    Youtube,
    Instagram,
    Twitter,
    Linkedin,
    Facebook,
    FileText,
    CheckCircle,
    AlertCircle,
    XCircle,
    RefreshCw,
    Unlink,
    Settings,
    Link2,
} from 'lucide-react';
import { PlatformConnection } from '../../../services/publishing.service';

interface PlatformConnectionCardProps {
    platform: PlatformConnection;
    onRefresh: (platform: PlatformConnection) => void;
    onDisconnect: (platform: PlatformConnection) => void;
    onSettings?: (platform: PlatformConnection) => void;
}

// Platform config for unconnected platforms
export interface PlatformConfig {
    id: string;
    platform: 'youtube' | 'instagram' | 'tiktok' | 'twitter' | 'linkedin' | 'facebook' | 'blog';
    name: string;
    color: string;
}

export const AVAILABLE_PLATFORMS: PlatformConfig[] = [
    { id: 'youtube', platform: 'youtube', name: 'YouTube', color: 'bg-red-500' },
    { id: 'instagram', platform: 'instagram', name: 'Instagram', color: 'bg-gradient-to-br from-pink-500 to-purple-600' },
    { id: 'tiktok', platform: 'tiktok', name: 'TikTok', color: 'bg-black' },
    { id: 'twitter', platform: 'twitter', name: 'Twitter/X', color: 'bg-gray-900' },
    { id: 'linkedin', platform: 'linkedin', name: 'LinkedIn', color: 'bg-blue-600' },
    { id: 'facebook', platform: 'facebook', name: 'Facebook', color: 'bg-blue-700' },
    { id: 'blog', platform: 'blog', name: 'Blog', color: 'bg-green-500' },
];

export const getPlatformIcon = (platform: string, className = 'w-5 h-5') => {
    const icons: Record<string, React.ReactNode> = {
        youtube: <Youtube className={className} />,
        instagram: <Instagram className={className} />,
        twitter: <Twitter className={className} />,
        linkedin: <Linkedin className={className} />,
        facebook: <Facebook className={className} />,
        tiktok: (
            <div className={`${className} bg-black dark:bg-white rounded-sm flex items-center justify-center text-white dark:text-black text-xs font-bold`}>
                T
            </div>
        ),
        blog: <FileText className={className} />,
    };
    return icons[platform] || <FileText className={className} />;
};

export const getPlatformColor = (platform: string) => {
    const colors: Record<string, string> = {
        youtube: 'bg-red-500',
        instagram: 'bg-gradient-to-br from-pink-500 to-purple-600',
        twitter: 'bg-gray-900 dark:bg-gray-100',
        linkedin: 'bg-blue-600',
        facebook: 'bg-blue-700',
        tiktok: 'bg-black dark:bg-white',
        blog: 'bg-green-500',
    };
    return colors[platform] || 'bg-gray-500';
};

export const getPlatformName = (platform: string) => {
    const names: Record<string, string> = {
        youtube: 'YouTube',
        instagram: 'Instagram',
        twitter: 'Twitter/X',
        linkedin: 'LinkedIn',
        facebook: 'Facebook',
        tiktok: 'TikTok',
        blog: 'Blog',
    };
    return names[platform] || platform;
};

const PlatformConnectionCard: React.FC<PlatformConnectionCardProps> = ({
    platform,
    onRefresh,
    onDisconnect,
    onSettings,
}) => {
    const getStatusInfo = (status: string) => {
        const config: Record<string, { style: string; icon: React.ReactNode; label: string }> = {
            connected: {
                style: 'text-green-600 dark:text-green-400',
                icon: <CheckCircle className="w-4 h-4" />,
                label: 'Connected',
            },
            auth_expired: {
                style: 'text-yellow-600 dark:text-yellow-400',
                icon: <AlertCircle className="w-4 h-4" />,
                label: 'Auth Expired',
            },
            disconnected: {
                style: 'text-gray-500 dark:text-gray-400',
                icon: <XCircle className="w-4 h-4" />,
                label: 'Disconnected',
            },
            error: {
                style: 'text-red-600 dark:text-red-400',
                icon: <XCircle className="w-4 h-4" />,
                label: 'Error',
            },
        };
        return config[status] || config.disconnected;
    };

    const formatLastSync = (dateString?: string) => {
        if (!dateString) return 'Never';
        const date = new Date(dateString);
        const now = new Date();
        const diffMs = now.getTime() - date.getTime();
        const diffMins = Math.floor(diffMs / (1000 * 60));
        const diffHrs = Math.floor(diffMs / (1000 * 60 * 60));
        const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

        if (diffMins < 1) return 'Just now';
        if (diffMins < 60) return `${diffMins} min${diffMins > 1 ? 's' : ''} ago`;
        if (diffHrs < 24) return `${diffHrs} hr${diffHrs > 1 ? 's' : ''} ago`;
        return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
    };

    const statusInfo = getStatusInfo(platform.status);

    return (
        <div className="bg-white dark:bg-gray-800 rounded-3xl shadow-[0_0_15px_rgba(0,0,0,0.07)] border border-gray-100 dark:border-gray-700 p-5 hover:shadow-[0_0_20px_rgba(0,0,0,0.1)] transition-all duration-200">
            <div className="flex items-start justify-between">
                {/* Platform Info */}
                <div className="flex items-center gap-4">
                    <div className={`w-12 h-12 ${getPlatformColor(platform.platform)} rounded-xl flex items-center justify-center text-white`}>
                        {getPlatformIcon(platform.platform, 'w-6 h-6')}
                    </div>
                    <div>
                        <h3 className="font-semibold text-gray-900 dark:text-white">
                            {getPlatformName(platform.platform)}
                        </h3>
                        <div className={`flex items-center gap-1 text-sm ${statusInfo.style}`}>
                            {statusInfo.icon}
                            <span>{statusInfo.label}</span>
                        </div>
                    </div>
                </div>
            </div>

            {/* Details */}
            <div className="mt-4 space-y-2">
                <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-500 dark:text-gray-400">Account</span>
                    <span className="font-medium text-gray-900 dark:text-white">@{platform.accountName}</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-500 dark:text-gray-400">Last Sync</span>
                    <span className="text-gray-700 dark:text-gray-300">{formatLastSync(platform.lastSync)}</span>
                </div>
                {platform.apiQuotaLimit && (
                    <div className="flex items-center justify-between text-sm">
                        <span className="text-gray-500 dark:text-gray-400">API Quota</span>
                        <span className="text-gray-700 dark:text-gray-300">
                            {platform.apiQuotaUsed?.toLocaleString() || 0}/{platform.apiQuotaLimit.toLocaleString()}
                        </span>
                    </div>
                )}
                {platform.postsLimit && (
                    <div className="flex items-center justify-between text-sm">
                        <span className="text-gray-500 dark:text-gray-400">Posts Today</span>
                        <span className="text-gray-700 dark:text-gray-300">
                            {platform.postsToday || 0}/{platform.postsLimit === -1 ? 'unlimited' : platform.postsLimit}
                        </span>
                    </div>
                )}
            </div>

            {/* Actions */}
            <div className="mt-4 pt-4 border-t border-gray-100 dark:border-gray-700 flex items-center gap-2">
                {platform.status === 'auth_expired' ? (
                    <button
                        onClick={() => onRefresh(platform)}
                        className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300 rounded-xl hover:bg-yellow-200 dark:hover:bg-yellow-900/50 transition-colors font-medium text-sm"
                    >
                        <Link2 className="w-4 h-4" />
                        Reconnect
                    </button>
                ) : (
                    <>
                        <button
                            onClick={() => onRefresh(platform)}
                            className="flex items-center gap-1.5 px-3 py-2 text-sm text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-xl transition-colors"
                        >
                            <RefreshCw className="w-4 h-4" />
                            Refresh
                        </button>
                        <button
                            onClick={() => onDisconnect(platform)}
                            className="flex items-center gap-1.5 px-3 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-xl transition-colors"
                        >
                            <Unlink className="w-4 h-4" />
                            Disconnect
                        </button>
                    </>
                )}
                {onSettings && (
                    <button
                        onClick={() => onSettings(platform)}
                        className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-xl transition-colors"
                    >
                        <Settings className="w-4 h-4" />
                    </button>
                )}
            </div>
        </div>
    );
};

// Unconnected Platform Card for showing available platforms to connect
interface UnconnectedPlatformCardProps {
    config: PlatformConfig;
    onConnect: (platform: PlatformConfig) => void;
}

export const UnconnectedPlatformCard: React.FC<UnconnectedPlatformCardProps> = ({
    config,
    onConnect,
}) => {
    return (
        <div className="bg-white dark:bg-gray-800 rounded-3xl shadow-[0_0_15px_rgba(0,0,0,0.07)] border border-gray-100 dark:border-gray-700 p-5 hover:shadow-[0_0_20px_rgba(0,0,0,0.1)] transition-all duration-200">
            <div className="flex items-center gap-4">
                <div className={`w-12 h-12 ${config.color} rounded-xl flex items-center justify-center text-white opacity-50`}>
                    {getPlatformIcon(config.platform, 'w-6 h-6')}
                </div>
                <div className="flex-1">
                    <h3 className="font-semibold text-gray-900 dark:text-white">{config.name}</h3>
                    <p className="text-sm text-gray-500 dark:text-gray-400">Not Connected</p>
                </div>
                <button
                    onClick={() => onConnect(config)}
                    className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-primary-600 to-indigo-600 text-white rounded-xl hover:from-primary-700 hover:to-indigo-700 transition-all text-sm font-medium"
                >
                    <Link2 className="w-4 h-4" />
                    Connect
                </button>
            </div>
        </div>
    );
};

export default PlatformConnectionCard;
